/**
 * Card Image Contrast Detector
 * Detects whether product card images are light or dark in the top-right and bottom-right corners.
 * Automatically toggles icon colors:
 * - Dark image background -> Light (white) icon
 * - Light image background -> Dark (navy) icon
 */

function calculateLuminance(data) {
  let totalLuminance = 0;
  let count = 0;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];
    if (a > 50) {
      // W3C BT.709 relative luminance formula
      const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      totalLuminance += luminance;
      count++;
    }
  }
  return count > 0 ? totalLuminance / count : 255;
}

export function updateGalleryContrast(gallery) {
  if (!gallery) return;

  // Find active image in gallery (support slideshow active slide or standard media image)
  const activeSlide =
    gallery.querySelector('slideshow-slide[aria-hidden="false"]') ||
    gallery.querySelector('slideshow-slide:not([hidden])') ||
    gallery;

  const img = activeSlide.querySelector('img.product-media__image, img');
  if (!img) return;

  const analyzeImage = (imgEl) => {
    if (!imgEl.naturalWidth || !imgEl.naturalHeight) return;

    const src = imgEl.currentSrc || imgEl.src;
    if (!src) return;

    // Avoid cross-origin canvas security errors by using anonymous crossOrigin Image
    const tempImg = new Image();
    tempImg.crossOrigin = 'anonymous';

    tempImg.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 50;
        canvas.height = 50;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return;

        ctx.drawImage(tempImg, 0, 0, 50, 50);

        // Top-Right region (Wishlist icon): X: 30..50, Y: 0..20
        const topRightData = ctx.getImageData(30, 0, 20, 20).data;
        const topRightLum = calculateLuminance(topRightData);

        // Bottom-Right region (Quick Add icon): X: 30..50, Y: 30..50
        const bottomRightData = ctx.getImageData(30, 30, 20, 20).data;
        const bottomRightLum = calculateLuminance(bottomRightData);

        // Luminance threshold < 145 => Dark region, else Light region
        const topState = topRightLum < 145 ? 'dark' : 'light';
        const bottomState = bottomRightLum < 145 ? 'dark' : 'light';

        gallery.setAttribute('data-top-brightness', topState);
        gallery.setAttribute('data-bottom-brightness', bottomState);

        const parentCard = gallery.closest('product-card, .product-card');
        if (parentCard) {
          parentCard.setAttribute('data-top-brightness', topState);
          parentCard.setAttribute('data-bottom-brightness', bottomState);
        }
      } catch (err) {
        // Fallback gracefully on any canvas error
      }
    };

    tempImg.onerror = () => {
      // Direct draw fallback if CORS fails
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 50;
        canvas.height = 50;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return;

        ctx.drawImage(imgEl, 0, 0, 50, 50);
        const topRightData = ctx.getImageData(30, 0, 20, 20).data;
        const bottomRightData = ctx.getImageData(30, 30, 20, 20).data;

        const topState = calculateLuminance(topRightData) < 145 ? 'dark' : 'light';
        const bottomState = calculateLuminance(bottomRightData) < 145 ? 'dark' : 'light';

        gallery.setAttribute('data-top-brightness', topState);
        gallery.setAttribute('data-bottom-brightness', bottomState);
      } catch (e) {}
    };

    tempImg.src = src;
  };

  if (img.complete && img.naturalWidth > 0) {
    analyzeImage(img);
  } else {
    img.addEventListener('load', () => analyzeImage(img), { once: true });
  }
}

export function initCardImageContrast() {
  const galleries = document.querySelectorAll('.card-gallery');
  galleries.forEach(updateGalleryContrast);
}

let debounceTimer = null;
function debouncedUpdate() {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    initCardImageContrast();
  }, 80);
}

// Observe DOM mutations for newly added product cards or image attribute changes
const observer = new MutationObserver((mutations) => {
  let shouldUpdate = false;
  for (const mutation of mutations) {
    if (mutation.addedNodes.length > 0 || mutation.type === 'attributes') {
      shouldUpdate = true;
      break;
    }
  }
  if (shouldUpdate) {
    debouncedUpdate();
  }
});

function attachEvents() {
  initCardImageContrast();

  // Listen to hover/interaction events on cards to re-evaluate on image slide changes
  document.body.addEventListener('pointerenter', (e) => {
    const card = e.target.closest && e.target.closest('.card-gallery, product-card');
    if (card) {
      setTimeout(() => {
        const gallery = card.classList.contains('card-gallery') ? card : card.querySelector('.card-gallery');
        if (gallery) updateGalleryContrast(gallery);
      }, 50);
    }
  }, true);

  document.body.addEventListener('pointerleave', (e) => {
    const card = e.target.closest && e.target.closest('.card-gallery, product-card');
    if (card) {
      setTimeout(() => {
        const gallery = card.classList.contains('card-gallery') ? card : card.querySelector('.card-gallery');
        if (gallery) updateGalleryContrast(gallery);
      }, 50);
    }
  }, true);

  document.body.addEventListener('click', (e) => {
    const swatchOrArrow = e.target.closest && e.target.closest('.swatch, .slideshow-control, [data-option-id], .product-card-stitching__swatch');
    if (swatchOrArrow) {
      setTimeout(() => {
        initCardImageContrast();
      }, 100);
      setTimeout(() => {
        initCardImageContrast();
      }, 300);
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src', 'srcset', 'aria-hidden', 'hidden'],
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', attachEvents);
} else {
  attachEvents();
}
